import {Injectable} from '@angular/core';
import {StateHttpService, StatesOrder} from '@api/http/state/state-http.service';
import {ImageService} from '@utils/services/image/image.service';
import {ImageSubType} from '@api/models/image/image-type';
import {PickerItem, PickerItemType} from '@lib-modules/picker/components/picker-item/picker-item.component';
import {TrendingHttpService} from '@api/http/trending/trending-http.service';
import {EntityType} from '@lib/models/entity/entity-type';
import {UserHttpService} from '@api/http/user/user-http.service';
import {UserService} from '@core/services/user.service';
import {LeagueHttpService} from '@api/http/league/league-http.service';
import {TeamsHttpService} from '@api/http/teams/teams-http.service';

@Injectable({
  providedIn: 'root',
})
export class PickerItemService {
  private readonly DEFAULT_PAGE_SIZE = 12;

  constructor(
    private stateHttp: StateHttpService,
    private trendingHttp: TrendingHttpService,
    private leagueHttp: LeagueHttpService,
    private teamHttp: TeamsHttpService,
    private userHttp: UserHttpService,
    private userService: UserService,
  ) {
  }

  /**
   * Get states as PickerItems
   */
  public async getStateItems(): Promise<PickerItem[]> {
    return this.fetchAndMapItems(
      this.stateHttp.getStatesSortedBy(StatesOrder.Size),
      PickerItemType.State,
      ImageSubType.COAT_OF_ARMS);
  }

  /**
   * Get top video leagues as PickerItems
   */
  public async getVideoLeagueItems(queryParams?: { stateId?: string }, pageSize = this.DEFAULT_PAGE_SIZE): Promise<PickerItem[]> {
    return this.fetchAndMapItems(this.trendingHttp.getTopByType(EntityType.League, 0, pageSize, queryParams), PickerItemType.League);
  }

  /**
   * Get top leagues for state as PickerItems
   */
  public async getTopLeaguesForState(stateId: string): Promise<PickerItem[]> {
    return this.fetchAndMapItems(this.leagueHttp.getTopLeaguesForState(stateId), PickerItemType.League);
  }

  /**
   * Get top teams of a state leagues as PickerItems
   */
  public async getTopTeamsForState(stateId: string): Promise<PickerItem[]> {
    return this.fetchAndMapItems(this.teamHttp.getTopTeamsForState(stateId), PickerItemType.Team);
  }

  /**
   * Get top national leagues as PickerItems
   */
  public async getNationalLeagueItems(): Promise<PickerItem[]> {
    return this.fetchAndMapItems(this.leagueHttp.getTopLeaguesNational(), PickerItemType.League);
  }

  /**
   * Get top international leagues as PickerItems
   */
  public async getInternationalLeagueItems(): Promise<PickerItem[]> {
    return this.fetchAndMapItems(this.leagueHttp.getTopLeaguesInternational(), PickerItemType.League);
  }

  /**
   * Get trending football teams as PickerItems
   */
  public async getFootballTeamItems(queryParams?: { stateId?: string }, pageSize = this.DEFAULT_PAGE_SIZE): Promise<PickerItem[]> {
    return this.fetchAndMapItems(this.trendingHttp.getTopByType(EntityType.Team, 0, pageSize, queryParams), PickerItemType.Team);
  }

  /**
   * Get trending handball teams grouped by leagues as PickerItems
   */
  public async getHandballTeamItems(pageSize = this.DEFAULT_PAGE_SIZE): Promise<PickerItem[][]> {
    return this.fetchAndMapGroupedItems(this.trendingHttp.getTopByType(EntityType.League, 0, pageSize), PickerItemType.Team);
  }

  /**
   * Get favorite items (teams + leagues) for the user
   */
  public async getFavoriteItems(): Promise<PickerItem[]> {
    if (!this.userService.isLoggedIn()) {
      return [];
    }
    const [favoriteTeams, favoriteLeagues] = await Promise.all([
      this.getFavoriteTeams(),
      this.getFavoriteLeagues(),
    ]);
    return [...favoriteTeams, ...favoriteLeagues];
  }

  /**
   * Get favorite teams as PickerItems
   */
  public async getFavoriteTeams(): Promise<PickerItem[]> {
    return this.fetchAndMapItems(this.userHttp.getFavoriteTeams(), PickerItemType.Team);
  }

  /**
   * Get favorite leagues as PickerItems
   */
  public async getFavoriteLeagues(): Promise<PickerItem[]> {
    return this.fetchAndMapItems(this.userHttp.getFavoriteLeagues(), PickerItemType.League);
  }

  /**
   * Utility to fetch data and map it to PickerItems
   */
  private async fetchAndMapItems(
    fetchFn: Promise<any>,
    type: PickerItemType,
    imageSubType: ImageSubType = ImageSubType.LOGO,
    addAllItem = false
  ): Promise<PickerItem[]> {
    try {
      const response = await fetchFn;
      const data = response?.data ?? response;
      const items = this.mapToPickerItems(data, type, imageSubType);
      if (addAllItem) {
        this.addAllItem(items);
      }
      return items;
    } catch (error) {
      console.error(`Error fetching ${type} items:`, error);
      return [];
    }
  }

  /**
   * Utility to fetch and map grouped items (e.g., handball teams by league)
   */
  private async fetchAndMapGroupedItems(
    fetchFn: Promise<any>,
    type: PickerItemType
  ): Promise<PickerItem[][]> {
    try {
      const leagues = await fetchFn;
      const leaguePromises = leagues.map(async (league) => {
        const teams = await this.trendingHttp.getTopByLeague(league._id, 0, 20);
        const teamItems = this.mapToPickerItems(teams, type);
        this.addAllItem(teamItems, PickerItemType.League, league._id);
        return teamItems;
      });
      return await Promise.all(leaguePromises);
    } catch (error) {
      console.error('Error fetching grouped items:', error);
      return [];
    }
  }

  /**
   * Maps raw data to PickerItems
   */
  private mapToPickerItems(
    items: any[],
    type: PickerItemType,
    imageSubType: ImageSubType = ImageSubType.LOGO,
  ): PickerItem[] {
    if (!items || items.length === 0) {
      return [];
    }
    return items.map((item) => ({
      objectId: item._id,
      readableId: item.readable_id,
      name: item.name,
      abbreviation: this.getAbbreviation(type, item),
      type,
      logo: ImageService.findPictureBySubType(item.pictures, imageSubType),
    }));
  }

  private getAbbreviation(type: PickerItemType, item: any): string {
    if (!item) {
      return undefined;
    }
    switch (type) {
      case PickerItemType.State:
        return item.shortname?.toUpperCase();
      case PickerItemType.Team:
        return item.abbreviation;
      default:
        return undefined;
    }
  }

  /**
   * Adds 'All' item at the beginning of the list, use type and id if there is an entity behind the all button f.e a league
   */
  private addAllItem(items: PickerItem[], type = PickerItemType.All, id?: string) {
    const allItem = {
      objectId: id,
      readableId: 'all',
      name: 'Alle',
      type: type,
      logo: null,
    };
    items.unshift(allItem);
  }
}
